import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Input,
  Card,
  message,
  Avatar,
  Space,
  Tooltip,
  Popconfirm,
  Modal,
  Tag,
  Divider,
  Empty,
  Spin,
  Rate,
  Drawer,
  Collapse,
  Badge,
  Segmented
} from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  SendOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  InboxOutlined,
  RobotOutlined,
  UserOutlined,
  LikeOutlined,
  DislikeOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
  FireOutlined,
  HistoryOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  RollbackOutlined,
  InfoCircleOutlined,
  CameraOutlined,
  DownloadOutlined,
  VerticalAlignBottomOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import API from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';
import './UserAIAssistant.css';

// 配置 dayjs
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('zh-cn');

const { TextArea } = Input;

const UserAIAssistant = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('active'); // 会话状态筛选: active/archived/all
  const [sessionSource, setSessionSource] = useState('all'); // 会话来源筛选: web/api/all
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [quickQueryVisible, setQuickQueryVisible] = useState(false);
  const [quickQueryIP, setQuickQueryIP] = useState('');
  const [quickQueryLoading, setQuickQueryLoading] = useState(false);
  const [faqVisible, setFaqVisible] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [hotQuestions, setHotQuestions] = useState([]);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [screenshotModalVisible, setScreenshotModalVisible] = useState(false);
  const [screenshotOptions, setScreenshotOptions] = useState({
    maskIPs: false,
    format: 'png',
    quality: 90
  });
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [isSelectingMessages, setIsSelectingMessages] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // 获取当前时区偏移（如 GMT+8）
  const getTimezoneOffset = () => {
    const offset = -new Date().getTimezoneOffset() / 60;
    const sign = offset >= 0 ? '+' : '';
    return `GMT${sign}${offset}`;
  };

  // 格式化时间为本地时区
  const formatLocalTime = (time, format = 'YYYY-MM-DD HH:mm:ss', showTimezone = false) => {
    if (!time) return '';
    // dayjs 默认使用本地时区，但我们显式转换以确保正确
    const formattedTime = dayjs(time).local().format(format);
    return showTimezone ? `${getTimezoneOffset()} ${formattedTime}` : formattedTime;
  };

  // 获取用户信息
  const fetchUserInfo = async () => {
    try {
      const response = await API.get('/api/user/profile');
      
      // 兼容两种API返回格式
      let userData;
      if (response.data.success) {
        // 格式1: { success: true, data: {...} }
        userData = response.data.data;
      } else if (response.data.username) {
        // 格式2: 直接返回用户数据 { username: "...", qq: "..." }
        userData = response.data;
      }
      
      if (userData) {
        setUserInfo(userData);
      } else {
        console.error('❌ 无法解析用户信息');
      }
    } catch (error) {
      console.error('❌ 获取用户信息错误:', error);
    }
  };

  // 获取AI助手状态
  const fetchAssistantStatus = async () => {
    try {
      const response = await API.get('/api/assistant/status');
      
      if (response.data.success) {
        const statusData = response.data.data;
        setIsMaintenanceMode(!statusData.isAvailable);
        setMaintenanceMessage(statusData.message || 'AI助手暂时不可用');
      }
    } catch (error) {
      console.error('获取AI助手状态错误:', error);
      // 如果获取失败，假设服务可用
      setIsMaintenanceMode(false);
    }
  };

  // 获取用户头像URL
  const getUserAvatar = () => {
    if (userInfo?.qq) {
      // 使用QQ头像API
      return `https://q1.qlogo.cn/g?b=qq&nk=${userInfo.qq}&s=100`;
    }
    return null; // 返回null使用默认图标
  };

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 获取会话列表
  const fetchSessions = async (status = sessionStatus, source = sessionSource) => {
    setSessionsLoading(true);
    try {
      const response = await API.get('/api/assistant/session/list', {
        params: { 
          limit: 50,
          status: status,
          source: source
        }
      });

      if (response.data.success) {
        const newSessions = [...response.data.data.list];
        // 创建新数组引用以确保 React 检测到变化
        setSessions(newSessions);
        
        // 如果当前有打开的会话，同步更新其信息
        if (currentSession) {
          const updatedCurrentSession = newSessions.find(s => s._id === currentSession._id);
          if (updatedCurrentSession) {
            setCurrentSession(prev => ({
              ...prev,
              messageCount: updatedCurrentSession.messageCount,
              lastMessageAt: updatedCurrentSession.lastMessageAt,
              updatedAt: updatedCurrentSession.updatedAt,
              title: updatedCurrentSession.title
            }));
          }
        }
      } else {
        message.error(response.data.message || '获取会话列表失败');
      }
    } catch (error) {
      console.error('获取会话列表错误:', error);
      message.error(error.response?.data?.message || '获取会话列表失败');
    } finally {
      setSessionsLoading(false);
    }
  };

  // 获取会话消息（初次加载）
  const fetchSessionMessages = async (sessionId, scrollToBottomAfter = true) => {
    setLoading(true);
    setIsInitialLoad(true);
    try {
      const response = await API.get(`/api/assistant/session/${sessionId}`, {
        params: {
          page: 1,
          limit: 50  // 每次加载50条
        }
      });

      if (response.data.success) {
        setCurrentSession(response.data.data.session);
        // 后端返回 _id, role, content, createdAt, updatedAt
        const messagesWithId = response.data.data.messages.map(msg => ({
          ...msg,
          _id: msg._id || msg.id, // 确保有ID
          isProcessing: false // 历史消息都已处理完成
        }));
        setMessages(messagesWithId);
        setCurrentPage(1);
        setHasMoreMessages(response.data.data.pagination?.hasMore || false);
        
        if (scrollToBottomAfter) {
          setTimeout(() => {
            scrollToBottom();
            // 延迟一点再允许自动加载，确保已经滚动到底部
            setTimeout(() => {
              setIsInitialLoad(false);
            }, 500);
          }, 100);
        } else {
          setIsInitialLoad(false);
        }
      } else {
        message.error(response.data.message || '获取消息失败');
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error('获取消息错误:', error);
      message.error(error.response?.data?.message || '获取消息失败');
      setIsInitialLoad(false);
    } finally {
      setLoading(false);
    }
  };

  // 加载更多历史消息
  const loadMoreMessages = useCallback(async () => {
    if (!currentSession || !hasMoreMessages || loadingMore) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await API.get(`/api/assistant/session/${currentSession._id}`, {
        params: {
          page: nextPage,
          limit: 50
        }
      });

      if (response.data.success) {
        const messagesWithId = response.data.data.messages.map(msg => ({
          ...msg,
          _id: msg._id || msg.id,
          isProcessing: false
        }));
        
        // 保存当前滚动位置和第一条消息的位置
        const container = messagesContainerRef.current;
        if (!container) return;
        
        const oldScrollHeight = container.scrollHeight;
        const oldScrollTop = container.scrollTop;
        
        // 使用 requestAnimationFrame 确保在下一帧更新
        requestAnimationFrame(() => {
          // 将新消息添加到开头
          setMessages(prev => [...messagesWithId, ...prev]);
          setCurrentPage(nextPage);
          setHasMoreMessages(response.data.data.pagination?.hasMore || false);
          
          // 使用 requestAnimationFrame 确保 DOM 更新后再调整滚动
          requestAnimationFrame(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              const heightDiff = newScrollHeight - oldScrollHeight;
              // 保持用户看到的内容位置不变
              container.scrollTop = oldScrollTop + heightDiff;
            }
          });
        });
      } else {
        message.error(response.data.message || '加载失败');
      }
    } catch (error) {
      console.error('加载更多消息错误:', error);
      message.error(error.response?.data?.message || '加载失败');
    } finally {
      // 延迟一点再设置 loadingMore 为 false，避免快速连续加载
      setTimeout(() => {
        setLoadingMore(false);
      }, 300);
    }
  }, [currentSession, hasMoreMessages, loadingMore, currentPage]);

  // 创建新会话
  const handleCreateSession = async () => {
    try {
      const response = await API.post('/api/assistant/session/create', {
        title: '新对话'
      });

      if (response.data.success) {
        message.success('创建成功');
        fetchSessions();
        setCurrentSession(response.data.data);
        setMessages([]);
      } else {
        message.error(response.data.message || '创建失败');
      }
    } catch (error) {
      console.error('创建会话错误:', error);
      message.error(error.response?.data?.message || '创建失败');
    }
  };

  // 轮询消息状态
  const pollMessageStatus = async (messageId, maxAttempts = 30) => {
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await API.get(`/api/assistant/message/${messageId}`);
        
        if (response.data.success) {
          const messageData = response.data.data;
          
          // 如果还在处理中，继续轮询
          if (messageData.isProcessing && attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 1000); // 每秒轮询一次
          } else {
            // 处理完成，更新消息内容
            setMessages(prev => prev.map(msg => 
              msg._id === messageId 
                ? { 
                    ...msg, 
                    content: messageData.content,
                    updatedAt: messageData.updatedAt,
                    isProcessing: false 
                  }
                : msg
            ));
            
            // 如果返回了会话标题，更新当前会话标题
            if (messageData.sessionTitle && currentSession) {
              setCurrentSession(prev => ({
                ...prev,
                title: messageData.sessionTitle
              }));
            }
            
            // 刷新会话列表以显示新标题
            fetchSessions();
            
            setTimeout(scrollToBottom, 100);
          }
        }
      } catch (error) {
        console.error('轮询消息状态错误:', error);
        // 如果是404或403错误，停止轮询
        if (error.response?.status === 404 || error.response?.status === 403) {
          console.error('消息不存在或无权访问，停止轮询');
          // 更新消息为错误状态
          setMessages(prev => prev.map(msg => 
            msg._id === messageId 
              ? { 
                  ...msg, 
                  content: '抱歉，获取回复失败，请重试。',
                  isProcessing: false 
                }
              : msg
          ));
        }
      }
    };
    
    poll();
  };

  // 发送消息（使用异步模式）
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) {
      message.warning('请输入消息内容');
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setSending(true);

    try {
      const response = await API.post('/api/assistant/chat', {
        sessionId: currentSession?._id,
        message: userMessage,
        async: true  // 使用异步模式
      });

      if (response.data.success) {
        const { sessionId, userMessage: savedUserMsg, assistantMessage } = response.data.data;

        // 如果是新会话，更新当前会话
        if (!currentSession || currentSession._id !== sessionId) {
          await fetchSessions();
          await fetchSessionMessages(sessionId);
        } else {
          // 添加消息到列表
          const newUserMsg = {
            ...savedUserMsg,
            sessionId: currentSession._id
          };
          
          const newAssistantMsg = {
            ...assistantMessage,
            sessionId: currentSession._id,
            isProcessing: assistantMessage.isProcessing || false
          };
          
          setMessages(prev => [...prev, newUserMsg, newAssistantMsg]);
          setTimeout(scrollToBottom, 100);
          
          // AI消息在处理中，开始轮询
          if (assistantMessage.isProcessing) {
            pollMessageStatus(assistantMessage._id);
          }
          
          // 更新会话列表
          fetchSessions();
        }
      } else {
        message.error(response.data.message || '发送失败');
        setInputMessage(userMessage); // 恢复输入
      }
    } catch (error) {
      console.error('发送消息错误:', error);
      message.error(error.response?.data?.message || '发送失败');
      setInputMessage(userMessage); // 恢复输入
    } finally {
      setSending(false);
    }
  };

  // 删除会话
  const handleDeleteSession = async (sessionId) => {
    try {
      const response = await API.delete(`/api/assistant/session/${sessionId}`);

      if (response.data.success) {
        message.success('删除成功');
        fetchSessions();
        if (currentSession?._id === sessionId) {
          setCurrentSession(null);
          setMessages([]);
        }
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除会话错误:', error);
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 归档/取消归档会话
  const handleArchiveSession = async (sessionId, archived = true) => {
    try {
      const response = await API.patch(`/api/assistant/session/${sessionId}/archive`, {
        archived
      });

      if (response.data.success) {
        message.success(archived ? '已归档' : '已取消归档');
        fetchSessions();
        // 如果取消归档的是当前会话，更新当前会话状态
        if (currentSession?._id === sessionId) {
          setCurrentSession(response.data.data);
        }
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('归档会话错误:', error);
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 更新会话标题
  const handleUpdateTitle = async (sessionId) => {
    if (!editTitle.trim()) {
      message.warning('标题不能为空');
      return;
    }

    try {
      const response = await API.patch(`/api/assistant/session/${sessionId}/title`, {
        title: editTitle
      });

      if (response.data.success) {
        message.success('更新成功');
        setEditingSessionId(null);
        setEditTitle('');
        fetchSessions();
        if (currentSession?._id === sessionId) {
          setCurrentSession(response.data.data);
        }
      } else {
        message.error(response.data.message || '更新失败');
      }
    } catch (error) {
      console.error('更新标题错误:', error);
      message.error(error.response?.data?.message || '更新失败');
    }
  };

  // 生成截图
  const handleGenerateScreenshot = async () => {
    if (!currentSession) {
      message.warning('请先选择一个会话');
      return;
    }

    if (selectedMessageIds.length > 0 && selectedMessageIds.length < 2) {
      message.warning('请至少选择2条消息');
      return;
    }

    setScreenshotLoading(true);
    try {
      const requestData = {
        maskIPs: screenshotOptions.maskIPs,
        format: screenshotOptions.format,
        quality: screenshotOptions.quality
      };

      // 如果选择了特定消息，使用消息ID列表；否则使用会话ID
      if (selectedMessageIds.length > 0) {
        requestData.messageIds = selectedMessageIds;
      } else {
        requestData.sessionId = currentSession._id;
      }

      const response = await API.post('/api/assistant/screenshot', requestData, {
        responseType: 'blob'
      });

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = selectedMessageIds.length > 0 
        ? `chat-selected-${Date.now()}.${screenshotOptions.format}`
        : `chat-${currentSession.title}-${Date.now()}.${screenshotOptions.format}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('截图已生成并下载');
      setScreenshotModalVisible(false);
      setIsSelectingMessages(false);
      setSelectedMessageIds([]);
    } catch (error) {
      console.error('生成截图错误:', error);
      message.error(error.response?.data?.message || '生成截图失败');
    } finally {
      setScreenshotLoading(false);
    }
  };

  // 快速查询 IP 黑洞状态
  const handleQuickQuery = async () => {
    if (!quickQueryIP.trim()) {
      message.warning('请输入IP地址');
      return;
    }

    setQuickQueryLoading(true);
    try {
      const response = await API.post('/api/assistant/quick-query/ip-blackhole', {
        ip: quickQueryIP
      });

      if (response.data.success) {
        const { ip, isBlackholed, status, message: statusMessage } = response.data.data;
        
        Modal.info({
          title: (
            <div style={{ fontSize: 16, fontWeight: 500 }}>
              <SearchOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              IP黑洞状态查询结果
            </div>
          ),
          width: 520,
          icon: null,
          centered: true,
          content: (
            <div style={{ padding: '16px 0' }}>
              <Card 
                bordered={false}
                style={{ 
                  background: isBlackholed ? '#fff2e8' : '#f6ffed',
                  borderRadius: 8
                }}
              >
                {/* IP 地址 */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ 
                    fontSize: 12, 
                    color: '#666', 
                    marginBottom: 8,
                    fontWeight: 500
                  }}>
                    IP 地址
                  </div>
                  <div style={{ 
                    fontSize: 24, 
                    fontWeight: 'bold', 
                    fontFamily: 'Consolas, Monaco, monospace',
                    color: '#262626',
                    letterSpacing: '0.5px'
                  }}>
                    {ip}
                  </div>
                </div>
                
                <Divider style={{ margin: '16px 0' }} />
                
                {/* 当前状态 */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ 
                    fontSize: 12, 
                    color: '#666', 
                    marginBottom: 12,
                    fontWeight: 500
                  }}>
                    当前状态
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Tag 
                      color={isBlackholed ? 'error' : 'success'} 
                      style={{ 
                        fontSize: 14, 
                        padding: '6px 16px',
                        margin: 0,
                        borderRadius: 4,
                        fontWeight: 500
                      }}
                      icon={isBlackholed ? <CloseOutlined /> : <CheckCircleOutlined />}
                    >
                      {isBlackholed ? '已被黑洞封禁' : '状态正常'}
                    </Tag>
                    <Badge 
                      status={isBlackholed ? 'error' : 'success'} 
                      text={
                        <span style={{ fontSize: 14, color: '#595959' }}>
                          {status === 'blackholed' ? '黑洞中' : 
                           status === 'normal' ? '正常' : 
                           status}
                        </span>
                      }
                    />
                  </div>
                </div>
                
                  {statusMessage && (
                    <>
                      <Divider style={{ margin: '16px 0' }} />
                      
                      {/* 详细信息 */}
                      <div>
                        <div style={{ 
                          fontSize: 12, 
                          color: '#666', 
                          marginBottom: 12,
                          fontWeight: 500
                        }}>
                          详细信息
                        </div>
                        <div style={{ 
                          padding: 12, 
                          background: 'rgba(255, 255, 255, 0.8)', 
                          borderRadius: 6,
                          fontSize: 14,
                          color: '#595959',
                          lineHeight: 1.8,
                          whiteSpace: 'pre-line'
                        }}>
                          {statusMessage}
                        </div>
                      </div>
                    </>
                  )}
              </Card>
              
              <div style={{ 
                textAlign: 'center', 
                color: '#bfbfbf', 
                fontSize: 12,
                marginTop: 16
              }}>
                查询时间: {formatLocalTime(new Date(), 'YYYY-MM-DD HH:mm:ss', true)}
              </div>
            </div>
          ),
          okText: '关闭',
          okButtonProps: {
            size: 'large',
            style: { minWidth: 100 }
          }
        });
        setQuickQueryVisible(false);
        setQuickQueryIP('');
      } else {
        // 处理失败响应，显示友好的错误提示
        const errorMsg = response.data.error || response.data.message || '查询失败';
        Modal.error({
          title: '查询失败',
          width: 480,
          centered: true,
          content: (
            <div style={{ padding: '16px 0' }}>
              <div style={{ 
                fontSize: 14, 
                color: '#595959',
                lineHeight: 1.8,
                marginBottom: 16
              }}>
                {errorMsg}
              </div>
              {quickQueryIP && (
                <div style={{ 
                  padding: 12,
                  background: '#fafafa',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#666'
                }}>
                  <div><strong>查询的IP：</strong>{quickQueryIP}</div>
                </div>
              )}
            </div>
          ),
          okText: '知道了'
        });
      }
    } catch (error) {
      console.error('快速查询错误:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || '查询失败，请稍后重试';
      Modal.error({
        title: '查询出错',
        width: 480,
        centered: true,
        content: (
          <div style={{ padding: '16px 0' }}>
            <div style={{ 
              fontSize: 14, 
              color: '#595959',
              lineHeight: 1.8,
              marginBottom: 16
            }}>
              {errorMsg}
            </div>
            {quickQueryIP && (
              <div style={{ 
                padding: 12,
                background: '#fafafa',
                borderRadius: 6,
                fontSize: 13,
                color: '#666'
              }}>
                <div><strong>查询的IP：</strong>{quickQueryIP}</div>
              </div>
            )}
          </div>
        ),
        okText: '知道了'
      });
    } finally {
      setQuickQueryLoading(false);
    }
  };

  // 获取 FAQ
  const fetchFAQ = async () => {
    try {
      const [faqRes, hotRes] = await Promise.all([
        API.get('/api/assistant/faq'),
        API.get('/api/assistant/hot-questions')
      ]);

      if (faqRes.data.success) {
        setFaqs(faqRes.data.data);
      }
      if (hotRes.data.success) {
        setHotQuestions(hotRes.data.data);
      }
    } catch (error) {
      console.error('获取FAQ错误:', error);
    }
  };

  // 提交反馈
  const handleSubmitFeedback = async (helpful) => {
    if (!feedbackMessage) return;

    try {
      const response = await API.post(
        `/api/assistant/message/${feedbackMessage._id}/feedback`,
        {
          helpful,
          rating: feedbackRating,
          comment: feedbackComment
        }
      );

      if (response.data.success) {
        message.success('感谢您的反馈！');
        setFeedbackModalVisible(false);
        setFeedbackMessage(null);
        setFeedbackRating(0);
        setFeedbackComment('');
        
        // 更新消息列表中的反馈状态
        setMessages(prev => prev.map(msg => 
          msg._id === feedbackMessage._id 
            ? { ...msg, feedback: { helpful, rating: feedbackRating, comment: feedbackComment } }
            : msg
        ));
      } else {
        message.error(response.data.message || '提交失败');
      }
    } catch (error) {
      console.error('提交反馈错误:', error);
      message.error(error.response?.data?.message || '提交失败');
    }
  };

  // 打开反馈弹窗
  const openFeedbackModal = (msg) => {
    setFeedbackMessage(msg);
    setFeedbackModalVisible(true);
  };

  // 处理FAQ/热门问题点击
  const handleFaqClick = async (questionTitle) => {
    setInputMessage(questionTitle);
    setFaqVisible(false);
    
    // 如果当前没有会话，自动创建一个
    if (!currentSession) {
      try {
        const response = await API.post('/api/assistant/session/create', {
          title: '新对话'
        });

        if (response.data.success) {
          const newSession = response.data.data;
          setCurrentSession(newSession);
          setMessages([]);
          await fetchSessions();
          message.success('已创建新对话');
        } else {
          message.error(response.data.message || '创建对话失败');
        }
      } catch (error) {
        console.error('创建会话错误:', error);
        message.error(error.response?.data?.message || '创建对话失败');
      }
    }
  };

  // 监听滚动事件，滚动到顶部时自动加载更多，显示返回底部按钮
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !currentSession || isInitialLoad) return;

    const handleScroll = () => {
      // 当滚动到距离顶部50px以内时，自动加载更多
      if (container.scrollTop < 50 && hasMoreMessages && !loadingMore && !loading) {
        loadMoreMessages();
      }

      // 判断是否显示返回底部按钮（距离底部超过300px时显示）
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 300;
      setShowScrollToBottom(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMoreMessages, loadingMore, loading, loadMoreMessages, currentSession, isInitialLoad]);

  // 初始加载
  useEffect(() => {
    fetchAssistantStatus();
    fetchSessions();
    fetchFAQ();
    fetchUserInfo();
    
    // 定期检查AI助手状态（每30秒）
    const statusCheckInterval = setInterval(() => {
      fetchAssistantStatus();
    }, 30000);
    
    return () => {
      clearInterval(statusCheckInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="ai-assistant-container" style={{ position: 'relative' }}>
      {/* 维护模式遮罩 */}
      {isMaintenanceMode && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(6px) saturate(120%)',
          WebkitBackdropFilter: 'blur(6px) saturate(120%)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: 80,
            height: 80,
            margin: '0 auto 24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
          }}>
            <RobotOutlined style={{ fontSize: 40, color: 'white' }} />
          </div>
          
          <h2 style={{
            fontSize: 28,
            fontWeight: 600,
            color: '#1f2937',
            marginBottom: 16,
            textShadow: '0 2px 8px rgba(255, 255, 255, 0.8)'
          }}>
            AI 助手维护中
          </h2>
          
          <p style={{
            fontSize: 16,
            color: '#374151',
            lineHeight: 1.8,
            marginBottom: 24,
            whiteSpace: 'pre-line',
            maxWidth: 500,
            textShadow: '0 1px 4px rgba(255, 255, 255, 0.8)'
          }}>
            {maintenanceMessage}
          </p>
          
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 24px',
            background: 'rgba(59, 130, 246, 0.12)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: 24,
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)'
          }}>
            <InfoCircleOutlined style={{ 
              fontSize: 16, 
              color: '#2563eb'
            }} />
            <span style={{
              fontSize: 14,
              color: '#1e40af',
              fontWeight: 500
            }}>
              如有紧急问题，请提交工单或联系管理员
            </span>
          </div>
        </div>
      )}
      
      {/* 侧边栏 - 会话列表 */}
      <div className="sessions-sidebar">
        <div className="sidebar-header">
          <h3>
            <RobotOutlined /> AI 助手
          </h3>
          <Space>
            <Tooltip title="常见问题">
              <Button
                type="text"
                icon={<QuestionCircleOutlined />}
                onClick={() => setFaqVisible(true)}
              />
            </Tooltip>
            <Tooltip title="快速查询">
              <Button
                type="text"
                icon={<SearchOutlined />}
                onClick={() => setQuickQueryVisible(true)}
              />
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateSession}
            >
              新对话
            </Button>
          </Space>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* 会话筛选器 */}
        <div style={{ padding: '0 12px 12px' }}>
          {/* 状态筛选 */}
          <Segmented
            value={sessionStatus}
            onChange={(value) => {
              setSessionStatus(value);
              fetchSessions(value, sessionSource);
            }}
            options={[
              { label: '活跃', value: 'active' },
              { label: '已归档', value: 'archived' },
              { label: '全部', value: 'all' }
            ]}
            block
            style={{ marginBottom: 8 }}
          />
          {/* 来源筛选 */}
          <Segmented
            value={sessionSource}
            onChange={(value) => {
              setSessionSource(value);
              fetchSessions(sessionStatus, value);
            }}
            options={[
              { label: '全部来源', value: 'all' },
              { label: 'Web端', value: 'web' },
              { label: 'API', value: 'api' }
            ]}
            block
          />
        </div>

        <div className="sessions-list">
          {sessionsLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin />
            </div>
          ) : sessions.length === 0 ? (
            <Empty
              description="暂无对话"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div>
              {sessions.map((session) => (
                <div
                  key={`${session._id}-${session.messageCount}-${session.lastMessageAt}`}
                  className={`session-item ${currentSession?._id === session._id ? 'active' : ''}`}
                  onClick={() => fetchSessionMessages(session._id)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '12px 10px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    marginBottom: '6px',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    background: currentSession?._id === session._id ? '#e6f7ff' : '#fff',
                    border: currentSession?._id === session._id ? '1px solid #91d5ff' : '1px solid #f0f0f0',
                    boxShadow: currentSession?._id === session._id ? '0 2px 4px rgba(24,144,255,0.1)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (currentSession?._id !== session._id) {
                      e.currentTarget.style.background = '#fafafa';
                      e.currentTarget.style.borderColor = '#d9d9d9';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSession?._id !== session._id) {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = '#f0f0f0';
                    }
                  }}
                >
                  <Avatar 
                    icon={<HistoryOutlined />}
                    size={36}
                    style={{ 
                      backgroundColor: session.status === 'active' ? '#52c41a' : '#d9d9d9',
                      marginRight: 10,
                      flexShrink: 0
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* 标题行 */}
                    {editingSessionId === session._id ? (
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onPressEnter={() => handleUpdateTitle(session._id)}
                        onBlur={() => {
                          setEditingSessionId(null);
                          setEditTitle('');
                        }}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <Tooltip title={session.title}>
                        <div style={{ 
                          fontWeight: currentSession?._id === session._id ? 600 : 'normal',
                          fontSize: 14,
                          marginBottom: 8,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#262626'
                        }}>
                          {session.title}
                        </div>
                      </Tooltip>
                    )}
                    
                    {/* 标签和时间行 */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 6,
                      marginBottom: 8
                    }}>
                      <Space size={4}>
                        <Tag 
                          color={session.status === 'active' ? 'success' : 'default'} 
                          style={{ 
                            margin: 0,
                            fontSize: 11,
                            padding: '0 6px',
                            lineHeight: '18px'
                          }}
                        >
                          {session.status === 'active' ? '活跃' : '已归档'}
                        </Tag>
                        <Tag 
                          color={session.source === 'api' ? 'blue' : 'orange'} 
                          style={{ 
                            margin: 0,
                            fontSize: 11,
                            padding: '0 6px',
                            lineHeight: '18px'
                          }}
                        >
                          {session.source === 'api' ? 'API' : 'Web'}
                        </Tag>
                      </Space>
                      <div style={{ 
                        fontSize: 11, 
                        color: '#8c8c8c',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatLocalTime(session.lastMessageAt || session.createdAt, 'MM-DD HH:mm')}
                      </div>
                    </div>
                    
                    {/* 操作按钮行 */}
                    <div style={{ 
                      display: 'flex', 
                      gap: 4,
                      flexWrap: 'wrap'
                    }}>
                      <Tooltip title="截图">
                        <Button
                          type="text"
                          size="small"
                          icon={<CameraOutlined />}
                          style={{ padding: '0 4px', height: 24 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (currentSession?._id === session._id) {
                              setScreenshotModalVisible(true);
                            } else {
                              fetchSessionMessages(session._id);
                              setTimeout(() => setScreenshotModalVisible(true), 500);
                            }
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="编辑标题">
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          style={{ padding: '0 4px', height: 24 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSessionId(session._id);
                            setEditTitle(session.title);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={session.status === 'active' ? '归档' : '取消归档'}>
                        <Button
                          type="text"
                          size="small"
                          icon={session.status === 'active' ? <InboxOutlined /> : <RollbackOutlined />}
                          style={{ padding: '0 4px', height: 24 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveSession(session._id, session.status === 'active');
                          }}
                        />
                      </Tooltip>
                      <Popconfirm
                        title="确定删除此对话吗？"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          handleDeleteSession(session._id);
                        }}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ padding: '0 4px', height: 24 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 主聊天区域 */}
      <div className="chat-main">
        {!currentSession ? (
          <div className="chat-empty">
            <Empty
              image={<RobotOutlined style={{ fontSize: 80, color: '#1890ff' }} />}
              description={
                <div>
                  <h2 style={{ color: '#1890ff', marginBottom: 16 }}>欢迎使用 AI 智能助手</h2>
                  <p style={{ fontSize: 16, marginBottom: 20 }}>我可以帮您：</p>
                  <div style={{ textAlign: 'left', display: 'inline-block', marginBottom: 24 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />解答系统使用相关问题</div>
                      <div><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />提供 IP 权限申请指导</div>
                      <div><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />快速查询 IP 黑洞状态</div>
                    </div>
                  </div>
                </div>
              }
            >
              <Space size="large">
                <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleCreateSession}>
                  开始新对话
                </Button>
                <Button size="large" icon={<QuestionCircleOutlined />} onClick={() => setFaqVisible(true)}>
                  查看常见问题
                </Button>
              </Space>
            </Empty>
          </div>
        ) : (
          <>
            {/* 聊天头部 */}
            <div className="chat-header">
              <div style={{ flex: 1 }}>
                <Space align="center">
                  <h3 style={{ margin: 0 }}>{currentSession.title}</h3>
                  <Tag color={currentSession.status === 'active' ? 'green' : 'default'}>
                    {currentSession.status === 'active' ? '活跃' : '已归档'}
                  </Tag>
                </Space>
                <Space size="large" style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: '#999' }}>
                    <HistoryOutlined style={{ marginRight: 4 }} />
                    创建于 {formatLocalTime(currentSession.createdAt, 'YYYY-MM-DD HH:mm:ss', true)}
                  </span>
                  {currentSession.lastMessageAt && (
                    <span style={{ fontSize: 12, color: '#999' }}>
                      最后活动 {formatLocalTime(currentSession.lastMessageAt, 'YYYY-MM-DD HH:mm:ss', true)}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: '#999' }}>
                    共 {currentSession.messageCount || 0} 条消息
                  </span>
                </Space>
              </div>
            </div>

            {/* 消息选择模式浮动操作栏 */}
            {isSelectingMessages && (
              <div style={{
                position: 'absolute',
                bottom: 80,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: 'white',
                padding: '12px 24px',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 16
              }}>
                <Space size="middle">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                    <span style={{ fontWeight: 500 }}>
                      已选择 <span style={{ color: '#1890ff' }}>{selectedMessageIds.length}</span> 条消息
                    </span>
                  </div>
                  <Divider type="vertical" style={{ height: 24 }} />
                  <Button
                    onClick={() => {
                      setIsSelectingMessages(false);
                      setSelectedMessageIds([]);
                    }}
                  >
                    取消
                  </Button>
                  <Button
                    type="primary"
                    icon={<CameraOutlined />}
                    onClick={() => setScreenshotModalVisible(true)}
                    disabled={selectedMessageIds.length < 2}
                  >
                    生成截图
                  </Button>
                </Space>
              </div>
            )}

            {/* 消息列表 */}
            <div className="messages-container" ref={messagesContainerRef}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin size="large" />
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Empty description="开始对话吧" />
                </div>
              ) : (
                <>
                  {/* 加载状态提示 */}
                  {loadingMore && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '8px 0',
                      marginBottom: 12,
                      opacity: 0.8,
                      transition: 'opacity 0.3s ease'
                    }}>
                      <Spin size="small" />
                      <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                        加载中...
                      </span>
                    </div>
                  )}
                  
                  {/* 没有更多消息提示 */}
                  {!hasMoreMessages && messages.length > 0 && !loadingMore && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '8px 0',
                      marginBottom: 12,
                      color: '#bfbfbf',
                      fontSize: 12,
                      transition: 'opacity 0.3s ease'
                    }}>
                      - 已加载全部消息 -
                    </div>
                  )}
                  
                  {messages.map((msg) => (
                    <div
                      key={msg._id}
                      className={`message-item ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
                      style={{
                        cursor: isSelectingMessages ? 'pointer' : 'default',
                        opacity: isSelectingMessages && !selectedMessageIds.includes(msg._id) ? 0.6 : 1,
                        border: isSelectingMessages && selectedMessageIds.includes(msg._id) ? '2px solid #1890ff' : 'none',
                        padding: isSelectingMessages && selectedMessageIds.includes(msg._id) ? '10px' : '12px',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        if (isSelectingMessages) {
                          if (selectedMessageIds.includes(msg._id)) {
                            setSelectedMessageIds(selectedMessageIds.filter(id => id !== msg._id));
                          } else {
                            setSelectedMessageIds([...selectedMessageIds, msg._id]);
                          }
                        }
                      }}
                    >
                      {msg.role === 'user' ? (
                        <Avatar
                          src={getUserAvatar()}
                          icon={<UserOutlined />}
                          style={{
                            backgroundColor: '#1890ff'
                          }}
                        />
                      ) : (
                        <Avatar
                          icon={<RobotOutlined />}
                          style={{
                            backgroundColor: '#52c41a'
                          }}
                        />
                      )}
                      <div className="message-content">
                        <div className="message-bubble">
                          {msg.isProcessing ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Spin size="small" />
                              <span style={{ color: '#999' }}>思考中...</span>
                            </div>
                          ) : msg.role === 'assistant' ? (
                            <div className="markdown-content">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  code({ node, inline, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || '');
                                    return !inline && match ? (
                                      <SyntaxHighlighter
                                        style={vscDarkPlus}
                                        language={match[1]}
                                        PreTag="div"
                                        {...props}
                                      >
                                        {String(children).replace(/\n$/, '')}
                                      </SyntaxHighlighter>
                                    ) : (
                                      <code className={className} {...props}>
                                        {children}
                                      </code>
                                    );
                                  },
                                  p({ children }) {
                                    return <p style={{ marginBottom: 12, lineHeight: 1.8 }}>{children}</p>;
                                  },
                                  br() {
                                    return <br />;
                                  }
                                }}
                              >
                                {msg.content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                          )}
                          
                          {/* 注意：历史消息只有 role, content, createdAt，不包含 intent 等字段 */}
                          {!msg.isProcessing && msg.intent && msg.intent !== 'processing' && (
                            <div style={{ marginTop: 8 }}>
                              <Tag color="blue" icon={<QuestionCircleOutlined />}>
                                意图: {msg.intent}
                              </Tag>
                            </div>
                          )}
                          
                          {!msg.isProcessing && msg.extractedParams && Object.keys(msg.extractedParams).length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              {Object.entries(msg.extractedParams).map(([key, value]) => (
                                <Tag key={key} color="cyan">
                                  {key}: {JSON.stringify(value)}
                                </Tag>
                              ))}
                            </div>
                          )}
                          
                          {!msg.isProcessing && msg.matchedKnowledgeId && (
                            <div style={{ marginTop: 8 }}>
                              <Tag color="green" icon={<CheckCircleOutlined />}>
                                已匹配知识库
                              </Tag>
                            </div>
                          )}
                          
                          {!msg.isProcessing && msg.apiResult && (
                            <div style={{ marginTop: 8 }}>
                              <Tag color="purple">
                                已执行API操作
                              </Tag>
                            </div>
                          )}
                        </div>
                        
                        <div className="message-meta">
                          <span>
                            {formatLocalTime(
                              msg.role === 'assistant' && msg.updatedAt ? msg.updatedAt : msg.createdAt,
                              'YYYY-MM-DD HH:mm:ss',
                              true
                            )}
                          </span>
                          
                          {msg.role === 'assistant' && !msg.isProcessing && (
                            <Space size="small">
                              {msg.feedback?.helpful !== undefined ? (
                                <Tag color={msg.feedback.helpful ? 'success' : 'error'}>
                                  {msg.feedback.helpful ? '有帮助' : '无帮助'}
                                </Tag>
                              ) : (
                                <>
                                  <Tooltip title="有帮助">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<LikeOutlined />}
                                      onClick={() => openFeedbackModal(msg)}
                                    />
                                  </Tooltip>
                                  <Tooltip title="无帮助">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<DislikeOutlined />}
                                      onClick={() => {
                                        setFeedbackMessage(msg);
                                        handleSubmitFeedback(false);
                                      }}
                                    />
                                  </Tooltip>
                                </>
                              )}
                            </Space>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* 返回底部按钮 */}
            {showScrollToBottom && !isSelectingMessages && (
              <div style={{
                position: 'absolute',
                bottom: 100,
                right: 24,
                zIndex: 100
              }}>
                <Tooltip title="返回底部">
                  <Button
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={<VerticalAlignBottomOutlined />}
                    onClick={scrollToBottom}
                    style={{
                      boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)'
                    }}
                  />
                </Tooltip>
              </div>
            )}

            {/* 输入区域 */}
            <div className="input-area">
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="输入您的问题... (Shift+Enter 换行，Enter 发送)"
                  disabled={sending}
                  style={{ 
                    fontSize: 14,
                    flex: 1
                  }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={sending}
                  disabled={!inputMessage.trim()}
                >
                  发送
                </Button>
              </div>
              
              {/* AI 提示信息 */}
              <div style={{ 
                textAlign: 'center', 
                marginTop: 12,
                fontSize: 12,
                color: '#999'
              }}>
                AI 也可能会犯错，生成的内容仅供参考。
              </div>
            </div>
          </>
        )}
      </div>

      {/* 快速查询抽屉 */}
      <Drawer
        title={
          <Space>
            <SearchOutlined />
            快速查询 - IP黑洞状态
          </Space>
        }
        placement="right"
        onClose={() => {
          setQuickQueryVisible(false);
          setQuickQueryIP('');
        }}
        open={quickQueryVisible}
        size="default"
        styles={{ body: { paddingTop: 24 } }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Card size="small" style={{ background: '#f0f5ff', border: '1px solid #adc6ff' }}>
            <Space direction="vertical" size="small">
              <div style={{ fontWeight: 'bold' }}>
                <QuestionCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                什么是IP黑洞？
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                当检测到大流量攻击时，系统会将受攻击的IP地址路由到黑洞，
                暂时屏蔽该IP的所有流量，以保护网络基础设施。
              </div>
            </Space>
          </Card>
          
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              输入 IP 地址
            </div>
            <Input
              size="large"
              placeholder="例如：103.217.194.205"
              value={quickQueryIP}
              onChange={(e) => setQuickQueryIP(e.target.value)}
              onPressEnter={handleQuickQuery}
              prefix={<SearchOutlined style={{ color: '#999' }} />}
              allowClear
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              支持 IPv4 地址格式
            </div>
          </div>
          
          <Button
            type="primary"
            block
            size="large"
            icon={<SearchOutlined />}
            onClick={handleQuickQuery}
            loading={quickQueryLoading}
            disabled={!quickQueryIP.trim()}
          >
            立即查询
          </Button>
        </Space>
      </Drawer>

      {/* FAQ 抽屉 */}
      <Drawer
        title="常见问题与热门问题"
        placement="right"
        onClose={() => setFaqVisible(false)}
        open={faqVisible}
        size="default"
        styles={{ body: { paddingTop: 24 } }}
      >
        <Collapse 
          defaultActiveKey={['1', '2']}
          items={[
            {
              key: '1',
              label: (
                <span>
                  <QuestionCircleOutlined /> 常见问题 (FAQ)
                </span>
              ),
              children: (
                <div>
                  {faqs.map((item) => (
                    <div
                      key={item._id}
                      style={{ 
                        cursor: 'pointer', 
                        padding: '12px',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background 0.3s'
                      }}
                      onClick={() => handleFaqClick(item.title)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>{item.title}</div>
                      {item.content && (
                        <div style={{ 
                          fontSize: 12, 
                          color: '#999', 
                          marginTop: 4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {item.content.substring(0, 50)}...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            },
            {
              key: '2',
              label: (
                <span>
                  <FireOutlined /> 热门问题
                </span>
              ),
              children: (
                <div>
                  {hotQuestions.map((item) => (
                    <div
                      key={item._id}
                      style={{ 
                        cursor: 'pointer', 
                        padding: '12px',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background 0.3s'
                      }}
                      onClick={() => handleFaqClick(item.title)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>{item.title}</div>
                      {item.content && (
                        <div style={{ 
                          fontSize: 12, 
                          color: '#999', 
                          marginTop: 4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {item.content.substring(0, 50)}...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            }
          ]}
        />
      </Drawer>

      {/* 反馈弹窗 */}
      <Modal
        title="消息反馈"
        open={feedbackModalVisible}
        onCancel={() => {
          setFeedbackModalVisible(false);
          setFeedbackMessage(null);
          setFeedbackRating(0);
          setFeedbackComment('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setFeedbackModalVisible(false);
              setFeedbackMessage(null);
              setFeedbackRating(0);
              setFeedbackComment('');
            }}
          >
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => handleSubmitFeedback(true)}
          >
            提交
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <p>这个回答对您有帮助吗？</p>
            <Rate
              value={feedbackRating}
              onChange={setFeedbackRating}
            />
          </div>
          <div>
            <p>您还有什么建议吗？（可选）</p>
            <TextArea
              rows={4}
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="请输入您的建议..."
            />
          </div>
        </Space>
      </Modal>

      {/* 截图配置弹窗 */}
      <Modal
        title={
          <Space>
            <CameraOutlined />
            <span>生成聊天截图</span>
          </Space>
        }
        open={screenshotModalVisible}
        onCancel={() => {
          setScreenshotModalVisible(false);
          setIsSelectingMessages(false);
          setSelectedMessageIds([]);
          setScreenshotOptions({
            maskIPs: false,
            format: 'png',
            quality: 90
          });
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setScreenshotModalVisible(false);
              setIsSelectingMessages(false);
              setSelectedMessageIds([]);
              setScreenshotOptions({
                maskIPs: false,
                format: 'png',
                quality: 90
              });
            }}
          >
            取消
          </Button>,
          <Button
            key="generate"
            type="primary"
            icon={<DownloadOutlined />}
            loading={screenshotLoading}
            onClick={handleGenerateScreenshot}
            disabled={isSelectingMessages && selectedMessageIds.length < 2}
          >
            生成并下载
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 截图范围选择 */}
          <div>
            <p style={{ marginBottom: 8, fontWeight: 500 }}>截图范围</p>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>截图方式</span>
                <Segmented
                  value={isSelectingMessages ? 'selected' : 'all'}
                  onChange={(value) => {
                    if (value === 'selected') {
                      setIsSelectingMessages(true);
                      setScreenshotModalVisible(false);
                      message.info('请在聊天界面点击选择要截图的消息');
                    } else {
                      setIsSelectingMessages(false);
                      setSelectedMessageIds([]);
                    }
                  }}
                  options={[
                    { label: '全部消息', value: 'all' },
                    { label: '选择消息', value: 'selected' }
                  ]}
                />
              </div>
              {isSelectingMessages && (
                <div style={{ 
                  background: '#e6f7ff', 
                  padding: 12, 
                  borderRadius: 4,
                  border: '1px solid #91d5ff'
                }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#1890ff' }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    已选择 {selectedMessageIds.length} 条消息
                    {selectedMessageIds.length < 2 && ' (至少选择2条)'}
                  </p>
                </div>
              )}
            </Space>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* 截图设置 */}
          <div>
            <p style={{ marginBottom: 8, fontWeight: 500 }}>截图设置</p>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>打码 IP 地址</span>
                <Button
                  size="small"
                  type={screenshotOptions.maskIPs ? 'primary' : 'default'}
                  onClick={() => setScreenshotOptions({
                    ...screenshotOptions,
                    maskIPs: !screenshotOptions.maskIPs
                  })}
                >
                  {screenshotOptions.maskIPs ? '已开启' : '已关闭'}
                </Button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>图片格式</span>
                <Segmented
                  value={screenshotOptions.format}
                  onChange={(value) => setScreenshotOptions({
                    ...screenshotOptions,
                    format: value
                  })}
                  options={[
                    { label: 'PNG', value: 'png' },
                    { label: 'JPEG', value: 'jpeg' }
                  ]}
                />
              </div>
              {screenshotOptions.format === 'jpeg' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span>图片质量</span>
                    <span style={{ color: '#1890ff' }}>{screenshotOptions.quality}%</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={screenshotOptions.quality}
                    onChange={(e) => setScreenshotOptions({
                      ...screenshotOptions,
                      quality: parseInt(e.target.value)
                    })}
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </Space>
          </div>

          <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              {isSelectingMessages 
                ? '将生成所选消息的截图' 
                : '将生成当前会话的所有消息截图'}
            </p>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default UserAIAssistant;

